import {
  type CollisionDetection,
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type Column,
  type ColumnRole,
  type ComputedSection,
  type ComputedTestRow,
  insertColumn,
  insertRow,
  moveColumn,
  moveRow,
  newId,
  newTestRow,
  type Row,
  removeColumn,
  removeRow,
  swapCells,
  type TemplateDoc,
  type TestRow,
} from '@quickreport/domain';
import clsx from 'clsx';
import { GripVertical, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import {
  minMaxOf,
  nextCode,
  rangeFromMinMax,
  rangesFromText,
  rangeText,
  simpleRange,
  updateColumn,
  updateRow,
  updateTest,
} from './rows';

const ROLE_LABELS: Record<ColumnRole, string> = {
  investigation: 'Investigation',
  result: 'Result',
  flag: 'Flag',
  ref_min: 'Ref. min',
  ref_max: 'Ref. max',
  ref_range: 'Reference',
  unit: 'Unit',
  method: 'Method',
  custom: 'Free text',
};

interface Props {
  doc: TemplateDoc;
  onChange: (next: TemplateDoc | ((prev: TemplateDoc) => TemplateDoc)) => void;
  computed: ComputedSection;
  examples: Record<string, string>;
  onExample: (rowId: string, value: string) => void;
  selectedRowId: string | null;
  onSelectRow: (id: string | null) => void;
  onError: (message: string) => void;
}

/** Only let a dragged item land on items of its own kind (row on row, column on column…). */
const sameKindCollision: CollisionDetection = (args) => {
  const kind = String(args.active.id).split(':')[0];
  return closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter((c) =>
      String(c.id).startsWith(`${kind}:`),
    ),
  });
};

export function GridEditor(props: Props) {
  const { doc, onChange } = props;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    const [kind, ...a] = String(e.active.id).split(':');
    const [, ...b] = String(e.over.id).split(':');
    if (kind === 'row') {
      onChange((d) =>
        moveRow(
          d,
          d.rows.findIndex((r) => r.id === a[0]),
          d.rows.findIndex((r) => r.id === b[0]),
        ),
      );
    } else if (kind === 'col') {
      onChange((d) =>
        moveColumn(
          d,
          d.columns.findIndex((c) => c.id === a[0]),
          d.columns.findIndex((c) => c.id === b[0]),
        ),
      );
    } else if (kind === 'cell') {
      onChange((d) =>
        swapCells(
          d,
          { rowId: a[0] as string, columnId: a[1] as string },
          { rowId: b[0] as string, columnId: b[1] as string },
        ),
      );
    }
  }

  const addRow = (at: number, kind: Row['kind'] = 'test') => {
    const row: Row =
      kind === 'test'
        ? newTestRow({ code: nextCode(doc), label: 'New investigation' })
        : kind === 'section'
          ? { id: newId(), kind: 'section', label: 'SECTION HEADING' }
          : { id: newId(), kind: 'note', text: 'Note' };
    onChange((d) => insertRow(d, at, row));
    props.onSelectRow(row.id);
  };

  const addColumn = (at: number) => onChange((d) => insertColumn(d, at, 'New column'));

  return (
    <DndContext sensors={sensors} collisionDetection={sameKindCollision} onDragEnd={onDragEnd}>
      <div className="overflow-x-auto rounded-lg border border-line bg-paper">
        <table className="w-full border-collapse text-left">
          <thead>
            <SortableContext
              items={doc.columns.map((c) => `col:${c.id}`)}
              strategy={horizontalListSortingStrategy}
            >
              <tr className="border-b border-line bg-canvas">
                <th className="w-8 px-1">
                  <IconButton label="Add a column at the start" onClick={() => addColumn(0)}>
                    <Plus size={14} />
                  </IconButton>
                </th>
                {doc.columns.map((c) => (
                  <ColumnHeader
                    key={c.id}
                    column={c}
                    onChange={(patch) => onChange((d) => updateColumn(d, c.id, patch))}
                    onRemove={() => {
                      try {
                        onChange(removeColumn(doc, c.id));
                      } catch (err) {
                        props.onError((err as Error).message);
                      }
                    }}
                  />
                ))}
                <th className="w-16 px-1 text-right">
                  <IconButton
                    label="Add a column at the end"
                    onClick={() => addColumn(doc.columns.length)}
                  >
                    <Plus size={14} />
                  </IconButton>
                </th>
              </tr>
            </SortableContext>
          </thead>
          <tbody>
            <tr>
              <td colSpan={doc.columns.length + 2} className="px-2 py-1">
                <button
                  type="button"
                  onClick={() => addRow(0)}
                  className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-line py-0.5 text-[12px] text-slate hover:border-brand hover:text-brand"
                >
                  <Plus size={13} /> Add a row at the top
                </button>
              </td>
            </tr>
            <SortableContext
              items={doc.rows.map((r) => `row:${r.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {doc.rows.map((r) => (
                <GridRow key={r.id} row={r} {...props} />
              ))}
            </SortableContext>
            <tr>
              <td colSpan={doc.columns.length + 2} className="px-2 py-2">
                <div className="flex flex-wrap gap-2">
                  <AddButton onClick={() => addRow(doc.rows.length, 'test')}>Add test</AddButton>
                  <AddButton onClick={() => addRow(doc.rows.length, 'section')}>
                    Add section heading
                  </AddButton>
                  <AddButton onClick={() => addRow(doc.rows.length, 'note')}>Add note</AddButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </DndContext>
  );
}

function ColumnHeader({
  column,
  onChange,
  onRemove,
}: {
  column: Column;
  onChange: (patch: Partial<Column>) => void;
  onRemove: () => void;
}) {
  const s = useSortable({ id: `col:${column.id}` });
  const style: CSSProperties = {
    transform: CSS.Translate.toString(s.transform),
    transition: s.transition,
  };
  return (
    <th
      ref={s.setNodeRef}
      style={style}
      className={clsx(
        'min-w-28 px-1.5 py-1.5 align-top',
        s.isDragging && 'relative z-10 bg-brand-soft',
      )}
    >
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          {...s.attributes}
          {...s.listeners}
          aria-label={`Move column ${column.label}`}
          className="cursor-grab rounded p-0.5 text-slate hover:bg-paper active:cursor-grabbing"
        >
          <GripVertical size={14} />
        </button>
        <input
          value={column.label}
          onChange={(e) => onChange({ label: e.target.value })}
          aria-label="Column heading"
          className="h-8 min-w-0 flex-1 rounded-md border border-line bg-paper px-2 text-[13px] font-semibold focus:border-brand focus:ring-2 focus:ring-brand/15 focus:outline-none"
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove column ${column.label}`}
          className="rounded p-0.5 text-slate hover:bg-paper hover:text-flag"
        >
          <X size={13} />
        </button>
      </div>
      <select
        value={column.role}
        onChange={(e) => onChange({ role: e.target.value as ColumnRole })}
        aria-label="What this column shows"
        className="mt-1 ml-5 h-7 w-[calc(100%-1.25rem)] rounded-md border border-line bg-paper px-1.5 text-[12px] font-normal text-slate focus:border-brand focus:outline-none"
      >
        {Object.entries(ROLE_LABELS).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
    </th>
  );
}

function GridRow(props: Props & { row: Row }) {
  const { row, doc, onChange, selectedRowId, onSelectRow } = props;
  const s = useSortable({ id: `row:${row.id}` });
  const style: CSSProperties = {
    transform: CSS.Translate.toString(s.transform),
    transition: s.transition,
  };
  const selected = selectedRowId === row.id;

  return (
    <tr
      ref={s.setNodeRef}
      style={style}
      onFocusCapture={() => onSelectRow(row.id)}
      className={clsx(
        'border-t border-line/70',
        selected && 'bg-brand-soft/40',
        s.isDragging && 'relative z-10 bg-paper shadow-md',
      )}
    >
      <td className="w-8 px-1 align-middle">
        <button
          type="button"
          {...s.attributes}
          {...s.listeners}
          aria-label="Move row"
          className="cursor-grab rounded p-0.5 text-slate hover:bg-canvas active:cursor-grabbing"
        >
          <GripVertical size={14} />
        </button>
      </td>
      {row.kind === 'test' ? (
        doc.columns.map((c) => <TestCell key={c.id} {...props} column={c} row={row} />)
      ) : (
        <td colSpan={doc.columns.length} className="px-1.5 py-1">
          {row.kind === 'section' ? (
            <CellInput
              value={row.label}
              onChange={(v) =>
                onChange((d) => updateRow(d, row.id, (r) => ({ ...r, label: v }) as Row))
              }
              className="font-bold tracking-wide uppercase"
              label="Section heading"
            />
          ) : row.kind === 'note' ? (
            <CellInput
              value={row.text}
              onChange={(v) =>
                onChange((d) => updateRow(d, row.id, (r) => ({ ...r, text: v }) as Row))
              }
              className="italic"
              label="Note"
            />
          ) : (
            <textarea
              value={row.text}
              onChange={(e) =>
                onChange((d) =>
                  updateRow(d, row.id, (r) => ({ ...r, text: e.target.value }) as Row),
                )
              }
              aria-label="Narrative text"
              rows={3}
              className="w-full rounded border border-line p-1.5 focus:border-brand focus:outline-none"
            />
          )}
        </td>
      )}
      <td className="w-16 px-1 text-right whitespace-nowrap">
        {row.kind === 'test' ? (
          <IconButton label="Row settings" onClick={() => onSelectRow(row.id)}>
            <SlidersHorizontal size={14} />
          </IconButton>
        ) : null}
        <IconButton
          label="Delete row"
          tone="danger"
          onClick={() => onChange((d) => removeRow(d, row.id))}
        >
          <Trash2 size={14} />
        </IconButton>
      </td>
    </tr>
  );
}

function TestCell({
  column,
  row,
  onChange,
  computed,
  examples,
  onExample,
}: Props & { column: Column; row: TestRow }) {
  const c = computed.rows.find(
    (x): x is ComputedTestRow => x.kind === 'test' && x.row.id === row.id,
  );
  const flagged = Boolean(c?.flag.code);
  const set = (patch: Partial<TestRow>) => onChange((d) => updateTest(d, row.id, patch));

  switch (column.role) {
    case 'investigation':
      return (
        <td className="px-1.5 py-1">
          <CellInput value={row.label} onChange={(v) => set({ label: v })} label="Investigation" />
          <div className="mt-0.5 flex gap-1 px-1 text-[11px] text-slate">
            <span className="font-mono">{row.code}</span>
            {row.method ? <span>· {row.method}</span> : null}
            {row.formula ? <span className="text-brand">· ƒ {row.formula}</span> : null}
          </div>
        </td>
      );
    case 'result':
      return (
        <td className="w-32 px-1.5 py-1">
          {row.formula ? (
            <div
              className={clsx(
                'tabular px-1.5 py-1 text-[13px]',
                flagged ? 'font-bold text-flag' : 'text-slate',
              )}
              title="Calculated from the formula"
            >
              {c?.display || <span className="text-slate/60">calculated</span>}
            </div>
          ) : (
            <CellInput
              value={examples[row.id] ?? ''}
              onChange={(v) => onExample(row.id, v)}
              placeholder="try a value"
              label={`Example result for ${row.label}`}
              className={clsx('tabular', flagged && 'font-bold text-flag')}
            />
          )}
          {c?.flag.code || c?.flag.band ? (
            <div className={clsx('px-1.5 text-[11px]', flagged ? 'text-flag' : 'text-slate')}>
              {[c.flag.code, c.flag.band].filter(Boolean).join(' · ')}
            </div>
          ) : null}
        </td>
      );
    case 'flag':
      return <td className="w-10 px-1.5 text-center font-bold text-flag">{c?.flag.code ?? ''}</td>;
    case 'ref_min':
    case 'ref_max': {
      const simple = simpleRange(row);
      if (simple === 'complex') {
        return (
          <td className="px-1.5 py-1 text-[12px] text-slate">
            {column.role === 'ref_min' ? (
              <span title={rangeText(row)}>{row.ranges.length} ranges · settings</span>
            ) : null}
          </td>
        );
      }
      const mm = minMaxOf(simple);
      return (
        <td className="w-28 px-1.5 py-1">
          <CellInput
            value={column.role === 'ref_min' ? mm.min : mm.max}
            inputMode="decimal"
            onChange={(v) =>
              set({
                ranges:
                  column.role === 'ref_min'
                    ? rangeFromMinMax(v, mm.max)
                    : rangeFromMinMax(mm.min, v),
              })
            }
            label={column.role === 'ref_min' ? 'Reference minimum' : 'Reference maximum'}
            className="tabular"
          />
        </td>
      );
    }
    case 'ref_range':
      return (
        <td className="min-w-36 px-1.5 py-1">
          {row.ranges.length > 1 || row.ranges[0]?.type === 'bands' || row.ranges[0]?.sex ? (
            <div className="px-1 text-[12px] whitespace-pre-line text-slate">{rangeText(row)}</div>
          ) : (
            <CellInput
              value={rangeText(row)}
              onChange={(v) => set({ ranges: rangesFromText(v) })}
              placeholder="e.g. 13 - 17, < 40"
              label="Reference range"
            />
          )}
        </td>
      );
    case 'unit':
      return (
        <td className="w-28 px-1.5 py-1">
          <CellInput
            value={row.unit ?? ''}
            onChange={(v) => set({ unit: v || undefined })}
            label="Unit"
          />
        </td>
      );
    case 'method':
      return (
        <td className="px-1.5 py-1">
          <CellInput
            value={row.method ?? ''}
            onChange={(v) => set({ method: v || undefined })}
            label="Method"
          />
        </td>
      );
    case 'custom':
      return <CustomCell row={row} column={column} onChange={onChange} />;
  }
}

/** Free-text cell that can be dragged onto another free-text cell to swap contents. */
function CustomCell({
  row,
  column,
  onChange,
}: {
  row: Row;
  column: Column;
  onChange: Props['onChange'];
}) {
  const id = `cell:${row.id}:${column.id}`;
  const drag = useDraggable({ id });
  const drop = useDroppable({ id });
  const style: CSSProperties = { transform: CSS.Translate.toString(drag.transform) };
  return (
    <td ref={drop.setNodeRef} className={clsx('px-1.5 py-1', drop.isOver && 'bg-brand-soft')}>
      <div
        ref={drag.setNodeRef}
        style={style}
        className={clsx(
          'group flex items-center gap-0.5',
          drag.isDragging && 'relative z-10 rounded bg-paper shadow',
        )}
      >
        <button
          type="button"
          {...drag.attributes}
          {...drag.listeners}
          aria-label="Move this cell"
          className="cursor-grab text-slate opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          <GripVertical size={12} />
        </button>
        <CellInput
          value={row.cells?.[column.id] ?? ''}
          onChange={(v) =>
            onChange((d) =>
              updateRow(d, row.id, (r) => ({ ...r, cells: { ...r.cells, [column.id]: v } })),
            )
          }
          label={column.label}
        />
      </div>
    </td>
  );
}

function CellInput({
  value,
  onChange,
  label,
  className,
  placeholder,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  className?: string;
  placeholder?: string;
  inputMode?: 'decimal' | 'text';
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      placeholder={placeholder}
      inputMode={inputMode}
      className={clsx(
        'h-8 w-full min-w-0 rounded-md border border-line bg-paper px-2 text-[13px] focus:border-brand focus:ring-2 focus:ring-brand/15 focus:outline-none',
        className,
      )}
    />
  );
}

function IconButton({
  label,
  onClick,
  children,
  tone,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={clsx(
        'rounded p-1 text-slate hover:bg-canvas',
        tone === 'danger' ? 'hover:text-flag' : 'hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-dashed border-line px-2.5 py-1 text-[13px] text-slate hover:border-brand hover:text-brand"
    >
      <Plus size={13} /> {children}
    </button>
  );
}
